import { useCallback, useState } from 'react';
import { useHandleApiResponse, useToast } from '../../../hooks';
import { useSubmitUrlMutation } from '../../../services';
import { isValidURL } from '../../../utils';
import { AlertType, TextButton, TextInput } from '../../shared';
import SubmitSuccess from './SubmitSuccess';

const SUBMIT_URL_ERROR_MSG = 'Failed to submit URL. Please try again later.';

const SubmitLink = () => {
  // State
  const [linkInput, setLinkInput] = useState('');
  const [error, setError] = useState('');
  const [isLinkSubmitted, setIsLinkSubmitted] = useState(false);

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Mutations
  const [submitUrl, { isLoading: isSubmittingUrl }] = useSubmitUrlMutation();

  const onLinkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkInput(e.target.value);
    setError('');
  }, []);

  const handleSubmitLink = useCallback(async () => {
    // If there is an error or the link is empty, return
    if (!linkInput || !!error) return;

    if (!isValidURL(linkInput)) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      const response = await submitUrl(linkInput);
      handleApiResponse({
        response,
        errorMsg: SUBMIT_URL_ERROR_MSG,
        hideSuccessToast: true, // Hide the success toast because we already show a success state.
        onSuccess: () => {
          setLinkInput('');
          setIsLinkSubmitted(true);
        },
      });
    } catch (error) {
      console.error(error);
      showToast({ message: SUBMIT_URL_ERROR_MSG, type: AlertType.ERROR });
    }
  }, [error, linkInput, submitUrl, handleApiResponse, isValidURL, showToast]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmitLink();
      }
    },
    [handleSubmitLink]
  );

  if (isLinkSubmitted) {
    return <SubmitSuccess handleSubmitAnother={() => setIsLinkSubmitted(false)} />;
  }

  return (
    <div className="flex gap-2">
      <TextInput placeholder="https://" value={linkInput} onChange={onLinkChange} error={error} onKeyDown={onKeyDown} />
      <TextButton
        text="Submit"
        onClick={handleSubmitLink}
        disabled={!linkInput}
        loading={isSubmittingUrl}
        className="mb-4"
      />
    </div>
  );
};

export default SubmitLink;
